import { useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation, Navigate } from "react-router-dom";
import { useAppSelector } from "../../store/hooks";
import { formatMoney } from "../../utils/formatMoney";

export default function TaxiTripForm() {
  const isAuthenticated = useAppSelector((s) => s.auth.isAuthenticated);
  const navigate = useNavigate();
  const location = useLocation();
  const { amount = 0, providerName = "", coverLabel = "Taxi", policyType = "Taxi", originalPolicy = null, insurance_provider_slug = null } = location.state || {};

  const [licenseNumber, setLicenseNumber] = useState("");
  const [agentCode, setAgentCode] = useState("");
  const [clientPhone, setClientPhone] = useState("");
  const [step, setStep] = useState(1);

  useEffect(() => { window.scrollTo(0, 0); }, [step]);

  const phoneValid = useMemo(() => /^0\d{9}$/.test(clientPhone), [clientPhone]);
  const licenseValid = useMemo(() => /[A-Za-z0-9-]{5,}/.test(licenseNumber), [licenseNumber]);
  const canContinue = phoneValid && licenseValid;

  if (!isAuthenticated) {
    return <Navigate to="/signin" replace state={{ from: "/taxi/form" }} />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white">
      <div className="pt-24 lg:pt-28" />
      <div className="px-4 pb-16 mx-auto max-w-5xl sm:px-6 lg:px-8">
        <div className="overflow-hidden relative p-6 mb-6 text-white bg-gradient-to-r from-yellow-500 to-emerald-600 rounded-2xl">
          <div className="flex relative z-10 flex-wrap gap-4 justify-between items-center">
            <div>
              <h1 className="text-2xl font-bold">Taxi Trip Details</h1>
              <p className="mt-1 text-sm text-white/85">Enter taxi and contact details to proceed to payment.</p>
            </div>
            <div className="flex gap-2">
              <button onClick={() => navigate(-1)} className="px-4 py-2 text-sm font-semibold rounded-xl border bg-white/10 hover:bg-white/20 border-white/20">Go Back</button>
            </div>
          </div>
          <div className="absolute -top-16 -right-16 w-48 h-48 rounded-full pointer-events-none bg-white/10" />
          <div className="absolute -left-12 -bottom-20 w-56 h-56 rounded-full pointer-events-none bg-white/10" />
        </div>

        <div className="flex gap-2 items-center mb-4 text-sm">
          <span className={`inline-flex justify-center items-center w-6 h-6 rounded-full text-white font-bold ${step === 1 ? "bg-emerald-600" : "bg-gray-400"}`}>1</span>
          <span>Trip Form</span>
          <span className="w-8 h-px bg-gray-300" />
          <span className={`inline-flex justify-center items-center w-6 h-6 rounded-full text-white font-bold ${step === 2 ? "bg-emerald-600" : "bg-gray-400"}`}>2</span>
          <span>Review</span>
        </div>

        {step === 1 && (
          <div className="p-5 bg-white rounded-2xl ring-1 ring-gray-200">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="sm:col-span-2">
                <label className="block mb-1 text-sm font-semibold text-gray-800">License number</label>
                <input value={licenseNumber} onChange={(e) => setLicenseNumber(e.target.value)} placeholder="e.g., GT-1234-23" className="px-4 py-3 w-full rounded-xl border border-gray-300 focus:ring-2 focus:ring-emerald-100 focus:border-emerald-400" />
              </div>
              <div>
                <label className="block mb-1 text-sm font-semibold text-gray-800">Agent code</label>
                <input value={agentCode} onChange={(e) => setAgentCode(e.target.value)} placeholder="Optional" className="px-4 py-3 w-full rounded-xl border border-gray-300 focus:ring-2 focus:ring-emerald-100 focus:border-emerald-400" />
              </div>
              <div>
                <label className="block mb-1 text-sm font-semibold text-gray-800">Client phone number</label>
                <input value={clientPhone} onChange={(e) => setClientPhone(e.target.value.replace(/[^0-9]/g, '').slice(0,10))} placeholder="0XXXXXXXXX" className={`w-full px-4 py-3 rounded-xl border ${phoneValid ? 'border-emerald-300' : 'border-gray-300'} focus:ring-2 focus:ring-emerald-100 focus:border-emerald-400`} />
                <p className="mt-1 text-xs text-gray-500">Enter 10-digit number starting with 0</p>
              </div>
            </div>

            <div className="flex justify-end mt-5">
              <button disabled={!canContinue} onClick={() => setStep(2)} className="px-5 py-3 font-semibold text-white bg-emerald-600 rounded-xl disabled:opacity-50">Continue</button>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-6">
            <div className="p-5 bg-white rounded-2xl ring-1 ring-gray-200">
              <h3 className="mb-3 text-lg font-semibold text-gray-900">Review taxi details</h3>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <Item k="License Number" v={licenseNumber} />
                <Item k="Agent Code" v={agentCode || '—'} />
                <Item k="Client Phone" v={clientPhone} />
              </div>
            </div>

            <div className="flex justify-between items-center p-4 bg-white rounded-2xl ring-1 ring-gray-200">
              <span className="text-sm text-gray-600">Amount to pay</span>
              <span className="text-2xl font-bold text-gray-900">{formatMoney(amount || 0, "₵ ")}</span>
            </div>

            <div className="flex justify-between mb-10">
              <button onClick={() => setStep(1)} className="px-5 py-3 font-semibold bg-gray-100 rounded-xl">Back</button>
              <button
                onClick={() => {
                  // Extract insurance_provider_slug if not already provided
                  const insuranceProviderSlug = insurance_provider_slug || (originalPolicy?.insurance_provider 
                    ? (typeof originalPolicy.insurance_provider === 'object' 
                        ? originalPolicy.insurance_provider.slug || originalPolicy.insurance_provider.id || null
                        : originalPolicy.insurance_provider)
                    : null);
                  navigate('/checkout', {
                    state: {
                      policyType,
                      providerName,
                      coverLabel,
                      amount,
                      insurance_provider_slug: insuranceProviderSlug,
                      trip: {
                        licenseNumber,
                        agentCode,
                        clientPhone,
                      }
                    }
                  });
                }}
                className="px-5 py-3 font-semibold text-white bg-emerald-600 rounded-xl"
              >
                Proceed to payment
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Item({ k, v }) {
  return (
    <div className="p-3 bg-gray-50 rounded-xl ring-1 ring-gray-200">
      <div className="text-xs text-gray-500">{k}</div>
      <div className="text-sm font-semibold text-gray-900">{v}</div>
    </div>
  );
}


